import { CloseOutlined } from '@ant-design/icons';
import { Drawer } from 'antd';
import StyledButton from 'app/components/StyledButton';
import useFetchData from 'app/hooks/useFetchData';
import { getCommonHotelDataApi } from 'app/services/holidayApi';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { formatAirport, formatNumber } from 'utils/helper';
import { selectTranslations } from '../App/slice/selectors';
import TooltipParagraph from '../Catalog/tooltipParagraph';
import ReadMore from './ReadMore';
import RoomDetailModal from './RoomDetailModal';
import SunWorldTicketsModal, {
  getTicketKey,
  type SunWorldTicketItem,
} from './SunWorldTicketsModal';
import {
  buildHotelPolicies,
  buildResolvedRoomOffer,
  calculateOfferPaxPrice,
  DrawerFHTFlightSummary,
  FHT_FALLBACK_IMAGE,
  FhtOfferOverride,
  FhtRoomOffer,
  getHotelAmenityTags,
  getStarCount,
  groupRoomOffersByType,
  normalizeRoomOffers,
  resolveDrawerHotelData,
} from './drawerOtherFHT.helpers';
import { LoadingContainer } from 'app/components/LoadingContainer';
import Images from 'app/assets/img';
import _ from 'lodash';
import { OverflowTags } from './components/OverflowTags';
import { getSelectedFlights } from 'utils/sessionStorageHelper';
import useWindowSize from 'app/hooks/useWindowSize';
import { COMBO_HOLIDAY_TYPES } from '../BookingMask';

export type { DrawerFHTFlightSummary } from './drawerOtherFHT.helpers';

interface IDrawerFHTProps {
  filter?: any;
  visible: boolean;
  onCloseDrawer: () => void;
  flightSummary?: DrawerFHTFlightSummary;
  onSelectCombo?: (payload: { hotel: any; roomOffer: any }) => void;
  isAllVisible: boolean;
  loading: boolean;
  comboType?: string;
}

type DrawerTab = 'rooms' | 'policies';

export default function DrawerOtherFHT(props: IDrawerFHTProps) {
  const {
    visible,
    onCloseDrawer,
    flightSummary,
    onSelectCombo,
    filter,
    isAllVisible = true,
    loading,
    comboType,
  } = props;
  const { device } = useWindowSize();
  const isMobile = device === 'mobile';
  const sessionFlights = getSelectedFlights();
  const translations = useSelector(selectTranslations);
  const isFH = comboType === COMBO_HOLIDAY_TYPES.FLIGHT_HOTEL;
  const [activeTab, setActiveTab] = useState<DrawerTab>('rooms');
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketModalTickets, setTicketModalTickets] = useState<
    SunWorldTicketItem[]
  >([]);
  const [ticketModalMode, setTicketModalMode] = useState<'info' | 'selection'>(
    'info',
  );
  const [ticketModalOfferKey, setTicketModalOfferKey] = useState<string | null>(
    null,
  );
  const [ticketModalPricing, setTicketModalPricing] = useState<any>(null);
  const [offerOverrides, setOfferOverrides] = useState<
    Record<string, FhtOfferOverride>
  >({});
  const [roomDetailOpen, setRoomDetailOpen] = useState(false);
  const [selectedRoomOffer, setSelectedRoomOffer] = useState<any>(null);
  const [hotelApiData, setHotelApiData] = useState<any>(null);
  const activeHotelIdRef = useRef<string | number | undefined>(undefined);
  const lastFetchKeyRef = useRef<string>('');
  const drawerBodyRef = useRef<HTMLDivElement>(null);
  const roomsSectionRef = useRef<HTMLDivElement>(null);
  const policiesSectionRef = useRef<HTMLDivElement>(null);
  const paxAdditionalAmountFlightSession = sessionFlights?.reduce((sum, f) => {
    return sum + Number(f?.pricing?.paxAdditionalAmount || 0);
  }, 0);

  const paxAdditionalAmountFlight = Number(
    paxAdditionalAmountFlightSession || 0,
  );
  const { onFetchData: onFetchHotelDetail, isLoading: isLoadingHotelDetail } =
    useFetchData({
      actionApi: getCommonHotelDataApi,
      IIFE: false,
      onSuccess: response => {
        // Response contract theo BE: { success: boolean, data: { hotel, roomTypes, ... } }
        const payload = response?.data || null;
        setHotelApiData(payload);
      },
      onError: () => {
        setHotelApiData(null);
      },
    });
  useEffect(() => {
    if (!isAllVisible) {
      setTicketModalOpen(false);
      setRoomDetailOpen(false);
    }
  }, [isAllVisible]);
  useEffect(() => {
    if (!visible) {
      lastFetchKeyRef.current = '';
      return;
    }
    if (!filter?.hotelId) return;

    const fetchKey = `${filter.sessionId || 'no-session'}-${filter.hotelId}`;
    if (lastFetchKeyRef.current === fetchKey) return;

    lastFetchKeyRef.current = fetchKey;
    activeHotelIdRef.current = filter.hotelId;
    setHotelApiData(null);
    onFetchHotelDetail({ payloadData: filter });
  }, [visible, filter?.sessionId, filter?.hotelId, onFetchHotelDetail]);

  const resolvedHotelData = useMemo(
    () => resolveDrawerHotelData(hotelApiData),
    [hotelApiData],
  );
  const resolvedFlightSummary = useMemo(
    () => flightSummary ?? { dateLabel: '', nights: 0 },
    [flightSummary],
  );
  const scrollToTab = useCallback((tab: DrawerTab) => {
    setActiveTab(tab);
    const target =
      tab === 'rooms' ? roomsSectionRef.current : policiesSectionRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!visible) return;
    setActiveTab('rooms');
    drawerBodyRef.current?.scrollTo({ top: 0 });
  }, [visible, resolvedHotelData?.hotel?.id]);

  useEffect(() => {
    setOfferOverrides({});
  }, [resolvedHotelData?.hotel?.id]);

  const hotel = resolvedHotelData?.hotel || {};
  const hotelImage = hotel?.images?.[0]?.url || FHT_FALLBACK_IMAGE;
  const starCount = getStarCount({ starRating: hotel?.starRating });
  const amenityTags = getHotelAmenityTags(hotel);
  const distanceLabel =
    hotel?.distanceFromCenter ||
    hotel?.distance ||
    hotel?.locationDescription ||
    '';

  const locationLine = useMemo(() => {
    const address = (hotel?.address || '').trim();
    const distance = (distanceLabel || '').trim();
    if (address && distance) return `${address} - ${distance}`;
    return address || distance;
  }, [hotel?.address, distanceLabel]);

  const roomGroups = useMemo(
    () => groupRoomOffersByType(normalizeRoomOffers(resolvedHotelData)),
    [resolvedHotelData],
  );

  const policyRows = useMemo(
    () => buildHotelPolicies(hotel, translations),
    [hotel, translations],
  );

  const renderPolicyValue = (row: {
    label: string;
    value: string;
    variant?: 'checkInOut';
  }) => {
    if (row.variant !== 'checkInOut') {
      return <PolicyValue>{row.value}</PolicyValue>;
    }

    const lines = row.value
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean);

    return (
      <PolicyValue>
        {lines.map((line, idx) => {
          const colonIndex = line.indexOf(':');
          if (colonIndex < 0) {
            return (
              <CheckInOutLine key={`${line}-${idx}`}>{line}</CheckInOutLine>
            );
          }
          const keyLabel = line.slice(0, colonIndex + 1).trim();
          const keyValue = line.slice(colonIndex + 1).trim();
          return (
            <CheckInOutLine key={`${line}-${idx}`}>
              <span>{keyLabel} </span>
              <PolicyStrong>{keyValue}</PolicyStrong>
            </CheckInOutLine>
          );
        })}
      </PolicyValue>
    );
  };

  useEffect(() => {
    const container = drawerBodyRef.current;
    const policiesEl = policiesSectionRef.current;
    if (!visible || !container || !policiesEl) return;

    const onScroll = () => {
      const containerTop = container.getBoundingClientRect().top;
      const policiesTop = policiesEl.getBoundingClientRect().top;
      setActiveTab(policiesTop <= containerTop + 100 ? 'policies' : 'rooms');
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [visible, policyRows.length]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = FHT_FALLBACK_IMAGE;
  };

  const openTicketsModal = (
    tickets: SunWorldTicketItem[],
    mode: 'info' | 'selection',
    offerKey?: string,
    pricing?: any,
  ) => {
    setTicketModalTickets(tickets);
    setTicketModalMode(mode);
    setTicketModalOfferKey(offerKey ?? null);
    setTicketModalPricing(pricing ?? null);
    setTicketModalOpen(true);
  };

  const closeTicketsModal = () => {
    setTicketModalOpen(false);
    setTicketModalTickets([]);
    setTicketModalMode('info');
    setTicketModalOfferKey(null);
    setTicketModalPricing(null);
  };

  const getDisplayTicketImage = (ticket?: any) =>
    ticket?.media?.[0] || FHT_FALLBACK_IMAGE;

  const handleSaveTicketSelection = (ticket: SunWorldTicketItem) => {
    if (!ticketModalOfferKey) return;

    const currentTickets =
      offerOverrides[ticketModalOfferKey]?.tickets ?? ticketModalTickets;
    const selectedKey = getTicketKey(ticket);
    const restTickets = currentTickets.filter(
      (item, index) => getTicketKey(item, index) !== selectedKey,
    );
    const reorderedTickets = [ticket, ...restTickets];

    const matchedOffer = roomGroups
      .flatMap(group => group.offers)
      .find(offer => offer.key === ticketModalOfferKey);
    const pricing = matchedOffer?.pricing ?? ticketModalPricing;
    const paxPrice = calculateOfferPaxPrice(pricing, ticket);

    setOfferOverrides(prev => ({
      ...prev,
      [ticketModalOfferKey]: {
        tickets: reorderedTickets,
        paxPrice,
        selectedTicket: ticket,
      },
    }));
  };
  const openRoomDetailModal = (offer: any) => {
    setSelectedRoomOffer(offer);
    setRoomDetailOpen(true);
  };
  const closeRoomDetailModal = () => {
    setRoomDetailOpen(false);
    setSelectedRoomOffer(null);
  };

  const handleSelectCombo = (offer: FhtRoomOffer) => {
    const roomOffer = buildResolvedRoomOffer(offer, offerOverrides[offer.key]);
    onSelectCombo?.({
      hotel: resolvedHotelData,
      roomOffer,
    });
    onCloseDrawer();
  };

  const renderStars = () => {
    if (!starCount) return null;
    return (
      <StarRow>
        {Array.from({ length: 5 }).map((_, i) => (
          <i
            key={i}
            className={`fa-solid fa-star${i < starCount ? '' : ' inactive'}`}
          />
        ))}
      </StarRow>
    );
  };

  const getTagLabel = (tag: string) => {
    if (tag === 'non_refundable') {
      return translations?.['b2b.txt.nonRefundable'];
    }
    return tag;
  };
  return (
    <>
      <Drawer
        title={null}
        width={isMobile ? '100%' : '50%'}
        onClose={onCloseDrawer}
        visible={visible}
        bodyStyle={{ padding: 0 }}
        closable={false}
        maskClosable={true}
        keyboard={true}
        headerStyle={{ display: 'none' }}
      >
        <LoadingContainer loading={loading}>
          <DrawerRoot>
            <DrawerHeader>
              <HeaderTop>
                <FlightSummary>
                  {(resolvedFlightSummary.departureAirport ||
                    resolvedFlightSummary.routeLabel) && (
                    <RouteText>
                      {resolvedFlightSummary.departureAirport ? (
                        <>
                          {formatAirport(
                            resolvedFlightSummary.departureAirport,
                          )}{' '}
                          {resolvedFlightSummary.destinationAirport && (
                            <>
                              <i
                                className={`fa-solid ${
                                  resolvedFlightSummary.stage === 'round-trip'
                                    ? 'fa-arrow-right-arrow-left'
                                    : 'fa-arrow-right'
                                }`}
                                style={{ fontSize: 12 }}
                              ></i>{' '}
                              {formatAirport(
                                resolvedFlightSummary.destinationAirport,
                              )}{' '}
                            </>
                          )}
                        </>
                      ) : (
                        resolvedFlightSummary.routeLabel
                      )}
                    </RouteText>
                  )}
                  {resolvedFlightSummary.dateLabel && (
                    <DateText>
                      {resolvedFlightSummary.dateLabel}
                      {resolvedFlightSummary.nights > 0 && (
                        <>
                          {' '}
                          | {resolvedFlightSummary.nights}{' '}
                          {translations?.['b2b.txt.nights']}
                        </>
                      )}
                    </DateText>
                  )}
                </FlightSummary>
                <CloseButton
                  type="button"
                  onClick={onCloseDrawer}
                  aria-label={translations?.['b2b.txt.close']}
                >
                  <CloseOutlined />
                </CloseButton>
              </HeaderTop>
            </DrawerHeader>
            {isLoadingHotelDetail ? (
              <LoadingWrapper>
                <LoadingImage src={Images.loadingCombo} alt="Loading ..." />
                <LoadingText>
                  {translations?.['b2b.txt.loadingResults']}
                </LoadingText>
              </LoadingWrapper>
            ) : !_.isEmpty(hotel) ? (
              <DrawerBody ref={drawerBodyRef}>
                <HotelHero>
                  <HotelInfo>
                    <HotelName ellipsis={{ rows: 1 }}>
                      {hotel?.name || ''}
                    </HotelName>
                    {renderStars()}
                    {locationLine && (
                      <HotelAddress ellipsis={{ rows: 1 }}>
                        {locationLine}
                      </HotelAddress>
                    )}
                    {amenityTags.length > 0 && (
                      <AmenityRow>
                        {amenityTags.map(tag => (
                          <AmenityTag key={tag}>{tag}</AmenityTag>
                        ))}
                      </AmenityRow>
                    )}
                  </HotelInfo>
                  <HotelThumb
                    src={hotelImage}
                    alt={hotel?.name}
                    onError={handleImageError}
                  />
                </HotelHero>

                <TabBar>
                  <TabButton
                    type="button"
                    $active={activeTab === 'rooms'}
                    onClick={() => scrollToTab('rooms')}
                  >
                    {translations?.['b2b.txt.rooms']}
                  </TabButton>
                  <TabButton
                    type="button"
                    $active={activeTab === 'policies'}
                    onClick={() => scrollToTab('policies')}
                  >
                    {translations?.['b2b.txt.policies']}
                  </TabButton>
                </TabBar>
                <RoomsPanel ref={roomsSectionRef}>
                  {roomGroups.length === 0 && (
                    <EmptyText>{translations?.['b2b.txt.noData']}</EmptyText>
                  )}
                  {roomGroups.map(group => (
                    <RoomGroup key={group.groupTitle}>
                      {group.offers.map(offer => {
                        const offerOverride = offerOverrides[offer.key];
                        const displayTickets =
                          offerOverride?.tickets ?? offer.tickets;
                        const displayPaxPrice =
                          +(offerOverride?.paxPrice ?? offer.paxPrice) +
                          paxAdditionalAmountFlight;
                        const firstTicket = displayTickets[0];
                        const isIncludeAllTicket =
                          offer?.pricing?.isIncludeAllTicket || false;
                        const extraTicketCount = Math.max(
                          0,
                          displayTickets.length - 1,
                        );
                        const roomImage =
                          offer.roomType?.images?.[0]?.url ||
                          FHT_FALLBACK_IMAGE;
                        const roomMeta = [
                          offer.roomType?.roomSize &&
                            `${offer.roomType.roomSize}m2`,
                          offer.roomType?.viewType,
                        ]
                          .filter(Boolean)
                          .join(', ');

                        return (
                          <OfferCard key={`${group.groupTitle}-${offer.key}`}>
                            <OfferHeader>
                              <OfferTitle ellipsis={{ rows: 2 }}>
                                {offer.roomType?.name}
                              </OfferTitle>
                              {roomMeta && <OfferMeta>{roomMeta}</OfferMeta>}
                              <DetailLink
                                type="button"
                                onClick={() => openRoomDetailModal(offer)}
                              >
                                {translations?.['b2b.txt.viewDetail']} &gt;
                              </DetailLink>
                            </OfferHeader>
                            <OfferBody $isFH={isFH}>
                              <RoomColumn>
                                <OfferThumb
                                  src={roomImage}
                                  alt={offer.roomType?.name}
                                  onError={handleImageError}
                                />
                                <RoomColumnInfo>
                                  <OfferTitle ellipsis={{ rows: 2 }}>
                                    {offer.pricing?.ratePlanName}
                                  </OfferTitle>
                                  {roomMeta && (
                                    <OfferMeta>{roomMeta}</OfferMeta>
                                  )}
                                  {/* <TagRow>
                                    {offer.tags.map(tag => (
                                      <OfferTag
                                        key={tag}
                                        $danger={tag === 'non_refundable'}
                                      >
                                        {getTagLabel(tag)}
                                      </OfferTag>
                                    ))}
                                  </TagRow> */}
                                  <OverflowTags
                                    tags={offer.tags.map(getTagLabel)}
                                  />
                                </RoomColumnInfo>
                              </RoomColumn>
                              {!isFH && (
                                <ComboColumn>
                                  <ComboThumb
                                    src={getDisplayTicketImage(firstTicket)}
                                    alt={firstTicket?.name}
                                    onError={handleImageError}
                                  />
                                  <ComboInfo>
                                    <ComboTitle ellipsis={{ rows: 1 }}>
                                      {firstTicket?.name || ''}
                                      {isIncludeAllTicket &&
                                        extraTicketCount > 0 &&
                                        ` +${extraTicketCount}`}
                                    </ComboTitle>
                                    {firstTicket?.description && (
                                      <ReadMore
                                        lines={3}
                                        html={firstTicket.description}
                                      />
                                    )}
                                    <TicketLink
                                      type="button"
                                      hidden={extraTicketCount === 0}
                                      onClick={() => {
                                        openTicketsModal(
                                          displayTickets,
                                          isIncludeAllTicket
                                            ? 'info'
                                            : 'selection',
                                          offer.key,
                                          offer.pricing,
                                        );
                                      }}
                                    >
                                      {isIncludeAllTicket
                                        ? translations?.[
                                            'b2b.txt.viewAllTickets'
                                          ]
                                        : translations?.[
                                            'b2b.txt.chooseOtherTicket'
                                          ]}{' '}
                                      &gt;
                                    </TicketLink>
                                  </ComboInfo>
                                </ComboColumn>
                              )}
                              <PriceColumn>
                                <PriceLabel>
                                  {
                                    translations?.[
                                      'b2b.txt.comboPricePerPerson'
                                    ]
                                  }
                                </PriceLabel>
                                <PriceValue>
                                  {formatNumber(displayPaxPrice, '.')}đ
                                </PriceValue>
                                <StyledButton
                                  textColor="#fff"
                                  height="40px"
                                  style={{
                                    maxWidth: 'fit-content',
                                    paddingLeft: 16,
                                    paddingRight: 16,
                                    borderRadius: 8,
                                    marginTop: 8,
                                  }}
                                  onClick={() => handleSelectCombo(offer)}
                                >
                                  {translations?.['b2b.txt.select']}
                                </StyledButton>
                              </PriceColumn>
                            </OfferBody>
                          </OfferCard>
                        );
                      })}
                    </RoomGroup>
                  ))}
                </RoomsPanel>

                <PoliciesSection ref={policiesSectionRef}>
                  <PolicySectionTitle>
                    {translations?.['b2b.txt.policies']}
                  </PolicySectionTitle>
                  <PoliciesCard>
                    {policyRows.length === 0 ? (
                      <EmptyText>{translations?.['b2b.txt.noData']}</EmptyText>
                    ) : (
                      policyRows.map(row => (
                        <PolicyRow key={row.label}>
                          <PolicyLabel>{row.label}</PolicyLabel>
                          {renderPolicyValue(row)}
                        </PolicyRow>
                      ))
                    )}
                  </PoliciesCard>
                </PoliciesSection>
              </DrawerBody>
            ) : (
              <NoData>{translations?.['b2b.txt.noDataFound']}</NoData>
            )}
          </DrawerRoot>
        </LoadingContainer>
      </Drawer>
      <SunWorldTicketsModal
        visible={ticketModalOpen}
        onClose={closeTicketsModal}
        tickets={ticketModalTickets}
        mode={ticketModalMode}
        selectedTicketId={
          ticketModalTickets[0]
            ? getTicketKey(ticketModalTickets[0])
            : undefined
        }
        onSave={
          ticketModalMode === 'selection'
            ? handleSaveTicketSelection
            : undefined
        }
      />
      <RoomDetailModal
        visible={roomDetailOpen}
        onClose={closeRoomDetailModal}
        roomOffer={selectedRoomOffer}
      />
    </>
  );
}

const DrawerRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
`;

const DrawerHeader = styled.div`
  border-bottom: 1px solid #e5e5e5;
  padding: 14px 16px;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const FlightSummary = styled.div`
  flex: 1;
  min-width: 0;
`;

const RouteText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #444;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #444;
  flex-shrink: 0;
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f8f8f8;
`;

const HotelHero = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  background: #fff;
  margin: -16px -16px 16px -16px;
  padding: 16px;
`;

const HotelInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const HotelName = styled(TooltipParagraph)`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin: 0 0 6px !important;
  max-width: 100%;
`;

const StarRow = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 6px;
  color: #f5a623;
  font-size: 12px;
  .inactive {
    color: #d4d4d4;
  }
`;

const HotelAddress = styled(TooltipParagraph)`
  font-size: 13px;
  color: #666;
  margin: 0 0 4px !important;
  max-width: 100%;
`;

const AmenityRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const AmenityTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  background: #f2f2f2;
  font-size: 12px;
  line-height: 1.35;
  color: #444;
  white-space: nowrap;
`;

const HotelThumb = styled.img`
  width: 120px;
  height: 90px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e5e5;
  margin: -16px -16px 16px -16px;
  background: #fff;
  padding: 0px 16px 0px 16px;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  padding: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: ${p => (p.$active ? '#000' : '#888')};
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    background: ${p => (p.$active ? '#f06f24' : 'transparent')};
  }
`;

const RoomsPanel = styled.div`
  scroll-margin-top: 12px;
`;

const RoomGroup = styled.div`
  margin-bottom: 20px;
`;

const GroupTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin: 0 0 10px;
`;

const OfferCard = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  background: #fff;
`;

const OfferHeader = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #e5e5e5;
`;

const OfferTitle = styled(TooltipParagraph)`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 0 !important;
  max-width: 100%;
`;

const OfferMeta = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

const DetailLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 4px;
  color: #f06f24;
  font-size: 12px;
  cursor: pointer;
`;

const OfferBody = styled.div<{ $isFH?: boolean }>`
  display: grid;
  grid-template-columns: ${props =>
    props.$isFH
      ? 'minmax(0, 1fr) auto'
      : `minmax(0, 1.1fr) minmax(0, 1.2fr) auto`};
  gap: 0;
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const RoomColumn = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  border-right: 1px solid #e5e5e5;
  @media (max-width: 992px) {
    border-right: none;
    border-bottom: 1px solid #e5e5e5;
  }
`;

const OfferThumb = styled.img`
  min-width: 72px;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const RoomColumnInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const OfferTag = styled.span<{ $danger?: boolean }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${p => (p.$danger ? '#fde8ea' : '#f2f2f2')};
  color: ${p => (p.$danger ? '#f06f24' : '#444')};
`;

const ComboColumn = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  border-right: 1px solid #e5e5e5;
  @media (max-width: 992px) {
    border-right: none;
    border-bottom: 1px solid #e5e5e5;
  }
`;

const ComboThumb = styled.img`
  min-width: 72px;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const ComboInfo = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 12px;
`;

const ComboTitle = styled(TooltipParagraph)`
  font-size: 13px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
`;

const TicketLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin-top: 6px;
  color: #f06f24;
  font-size: 12px;
  cursor: pointer;
`;

const PriceColumn = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  min-width: 160px;
`;

const PriceLabel = styled.div`
  font-size: 12px;
  color: #666;
`;

const PriceValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #000;
  margin-top: 2px;
`;

const PoliciesSection = styled.section`
  margin-top: 24px;
  scroll-margin-top: 12px;
`;

const PolicySectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin: 0 0 12px;
`;

const PoliciesCard = styled.div`
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 4px 16px;
`;

const PolicyRow = styled.div`
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const PolicyLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000;
`;

const PolicyValue = styled.div`
  font-size: 13px;
  color: #444;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const CheckInOutLine = styled.div`
  margin: 0;
  line-height: 1.6;
`;

const PolicyStrong = styled.span`
  font-weight: 700;
  color: #111;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #888;
  text-align: center;
  padding: 24px 0;
`;
const LoadingWrapper = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const LoadingImage = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 16px;
  object-fit: fill;
`;

const LoadingText = styled.div`
  font-size: 14px;
  text-align: center;
  color: '#8D8D8D';
`;
const NoData = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 14px;
`;
